import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Upload, CheckCircle2, AlertCircle, Trash2, ChevronDown, Bell, Layers, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { setWallpapers, getAllWallpapers } from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { ADMIN_ADDRESS } from '@/lib/constants';
import { useAuth, getIdToken } from '@pooflabs/web';
import { AuthContextType } from '@/components/types';
import { getPoofAPIUrl, getPoofAPIHeaders } from '@/lib/config';
import { loadApiKey, analyzeWallpaperImage } from '@/utils/adminApiKey';
import { CategoryNewButton } from '@/components/CategoryNewButton';
import { processWallpaperImage } from '@/utils/processWallpaperImage';

const BASE_WALLPAPER_CATEGORIES = ['All', 'Nature', 'Abstract', 'Minimal', 'Dark', 'Gradient', 'Space', 'Urban', 'Cyberpunk'];

type MediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

// Map each media type to the MIME prefix(es) it accepts
function getAcceptedMimePrefix(mediaType: MediaType): string {
  switch (mediaType) {
    case 'live-wallpaper': return 'video/';
    case 'ringtone': return 'audio/';
    case 'wallpaper':
    case 'icon':
    default: return 'image/';
  }
}

// The <input accept="..."> attribute value for each type
function getAcceptAttr(mediaType: MediaType): string {
  switch (mediaType) {
    case 'live-wallpaper': return 'video/*';
    case 'ringtone': return 'audio/*,.mp3,.wav,.ogg,.m4a,.aac,.flac';
    case 'wallpaper':
    case 'icon':
    default: return 'image/*';
  }
}

// Human-readable label for error messages
function getTypeLabel(mediaType: MediaType): string {
  switch (mediaType) {
    case 'live-wallpaper': return 'video';
    case 'ringtone': return 'audio';
    case 'wallpaper': return 'image';
    case 'icon': return 'image';
  }
}

// Human-readable section name shown in the modal subtitle
function getSectionName(mediaType: MediaType): string {
  switch (mediaType) {
    case 'live-wallpaper': return 'Live Wallpapers';
    case 'ringtone': return 'Ringtones';
    case 'wallpaper': return 'Wallpapers';
    case 'icon': return 'Icons';
  }
}

type ItemStatus = 'pending' | 'uploading' | 'done' | 'error' | 'duplicate';

interface BulkItem {
  id: string;
  file: File;
  localPreview: string;
  title: string;
  category: string;
  price: number | '';
  status: ItemStatus;
  hostedUrl?: string;
  error?: string;
  rawUrl?: string;
  isAnalyzing?: boolean;
  aiAnalyzed?: boolean;
}

function filenameToTitle(filename: string): string {
  const base = filename.replace(/\.[^/.]+$/, ''); // strip extension
  return base
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

interface AdminBulkUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToAdmin?: () => void;
  existingCategories?: string[];
  existingLiveWallpaperCategories?: string[];
  existingRingtoneCategories?: string[];
  mediaType: MediaType;
  onNewCategory?: (mediaType: 'wallpaper' | 'live-wallpaper' | 'ringtone', category: string) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.05)',
  color: '#f0f0f5',
  fontSize: 11,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: "'Syne', sans-serif",
};

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: 'rgba(167,139,250,0.7)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 3,
  display: 'block',
  fontFamily: "'Syne', sans-serif",
};

export const AdminBulkUpload: React.FC<AdminBulkUploadProps> = ({ isOpen, onClose, onNavigateToAdmin, existingCategories, existingLiveWallpaperCategories, existingRingtoneCategories, mediaType, onNewCategory }) => {
  const { user } = useAuth() as AuthContextType;
  const [items, setItems] = useState<BulkItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [defaultCategory, setDefaultCategory] = useState('All');
  const [defaultPrice, setDefaultPrice] = useState<number | ''>('');
  const [useAI, setUseAI] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [dupCount, setDupCount] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Build category list based on media type, merging persistent + session-local custom categories
  const allCategories = mediaType === 'live-wallpaper'
    ? [...new Set([...(existingLiveWallpaperCategories ?? []), ...customCategories])]
    : mediaType === 'ringtone'
    ? [...new Set([...(existingRingtoneCategories ?? []), ...customCategories])]
    : mediaType === 'icon'
    ? [...new Set([...customCategories])]
    : [...new Set([...BASE_WALLPAPER_CATEGORIES, ...(existingCategories ?? []), ...customCategories])];
  const acceptedMimePrefix = getAcceptedMimePrefix(mediaType);
  const acceptAttr = getAcceptAttr(mediaType);
  const typeLabel = getTypeLabel(mediaType);

  // Load API key from database when modal opens
  useEffect(() => {
    if (isOpen) {
      loadApiKey().then(key => setApiKey(key));
    }
  }, [isOpen]);

  // Reset custom category state and defaultCategory when mediaType changes
  useEffect(() => {
    setCustomCategories([]);
    if (mediaType === 'live-wallpaper') {
      const existing = existingLiveWallpaperCategories ?? [];
      setDefaultCategory(existing.length > 0 ? existing[0] : '');
    } else if (mediaType === 'ringtone') {
      const existing = existingRingtoneCategories ?? [];
      setDefaultCategory(existing.length > 0 ? existing[0] : '');
    } else if (mediaType === 'icon') {
      setDefaultCategory('');
    } else {
      setDefaultCategory('All');
    }
  }, [mediaType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset useAI to false when items list becomes empty
  useEffect(() => {
    if (items.length === 0 && useAI) {
      setUseAI(false);
    }
  }, [items.length, useAI]);

  const removeItem = (id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item) URL.revokeObjectURL(item.localPreview);
      return prev.filter(i => i.id !== id);
    });
  };

  const updateItem = (id: string, patch: Partial<BulkItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  };

  const applyDefaults = () => {
    setItems(prev => prev.map(i => i.status === 'pending' ? { ...i, category: defaultCategory, ...(defaultPrice !== '' ? { price: defaultPrice } : { price: '' }) } : i));
  };

  // Analyze a set of items using OpenAI Vision directly, then fallback to upload+AI suggest.
  const analyzeItems = useCallback(async (itemsToAnalyze: BulkItem[]) => {
    const token = await getIdToken();
    if (!token || !user?.address) return;

    const currentKey = apiKey;
    const isOpenAIKey = currentKey.trim().startsWith('sk-proj-') ||
      (currentKey.trim().startsWith('sk-') && !currentKey.trim().startsWith('sk-ant-') && currentKey.trim().length >= 48);

    // Mark all as analyzing
    const ids = new Set(itemsToAnalyze.map(i => i.id));
    setItems(prev => prev.map(i => ids.has(i.id) ? { ...i, isAnalyzing: true } : i));

    // Run all analyses in parallel
    await Promise.all(itemsToAnalyze.map(async (item) => {
      try {
        // Step 1: Upload to get a hosted URL (needed for OpenAI Vision or backend endpoint)
        let rawUrl = item.rawUrl;
        if (!rawUrl) {
          const formData = new FormData();
          formData.append('file', item.file);

          const uploadResponse = await fetch(getPoofAPIUrl('/api/upload-image'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Wallet-Address': user!.address,
              ...getPoofAPIHeaders(),
            },
            body: formData,
          });

          const uploadJson = await uploadResponse.json() as { success: boolean; data?: { url: string }; error?: { message: string } };
          if (!uploadJson.success || !uploadJson.data?.url) {
            throw new Error(uploadJson.error?.message || 'Upload failed');
          }
          rawUrl = uploadJson.data.url.split('?')[0];
        }

        let title: string | undefined;
        let category: string | undefined;

        // Step 2a: Try OpenAI Vision directly
        if (currentKey.trim() && isOpenAIKey) {
          const result = await analyzeWallpaperImage(rawUrl, currentKey.trim());
          if (result) {
            title = result.title;
            category = result.category;
          }
        }

        setItems(prev => prev.map(i => {
          if (i.id !== item.id) return i;
          return {
            ...i,
            rawUrl,
            title: title ?? i.title,
            category: category ?? i.category,
            isAnalyzing: false,
            aiAnalyzed: !!(title || category),
          };
        }));
      } catch {
        // AI failed — clear loading state but keep filename-derived title
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, isAnalyzing: false } : i));
      }
    }));
  }, [user, apiKey]);

  const addFiles = useCallback(async (files: File[]) => {
    const rejected: string[] = [];
    const validFiles = files.filter(f => {
      if (!f.type.startsWith(acceptedMimePrefix)) {
        rejected.push(f.name);
        return false;
      }
      return true;
    });

    if (rejected.length > 0) {
      toast.error(
        `${rejected.length} file${rejected.length !== 1 ? 's' : ''} rejected — only ${typeLabel} files are allowed here.`
      );
    }

    if (validFiles.length === 0) return;

    // Normalize wallpaper images to the fixed 1400x3100 cover-crop once, at add time,
    // so the AI pre-analysis upload and the final upload both reuse the same processed
    // file. Icons/live-wallpapers/ringtones pass through untouched.
    const processedFiles = mediaType === 'wallpaper'
      ? await Promise.all(validFiles.map(f => processWallpaperImage(f)))
      : validFiles;

    const newItems: BulkItem[] = processedFiles.map((file, idx) => ({
      id: `bulk-${Date.now()}-${idx}-${Math.random().toString(36).slice(2)}`,
      file,
      localPreview: URL.createObjectURL(file),
      title: filenameToTitle(file.name),
      category: defaultCategory,
      price: '',
      status: 'pending',
    }));

    setItems(prev => [...prev, ...newItems]);
  }, [defaultCategory, acceptedMimePrefix, typeLabel, mediaType]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const handleUploadAll = async () => {
    const pending = items.filter(i => i.status === 'pending');
    if (pending.length === 0) return;

    const token = await getIdToken();
    if (!token || !user?.address) {
      toast.error('Authentication required');
      return;
    }

    const currentKey = apiKey;
    const isOpenAIKey = currentKey.trim().startsWith('sk-proj-') ||
      (currentKey.trim().startsWith('sk-') && !currentKey.trim().startsWith('sk-ant-') && currentKey.trim().length >= 48);

    // Persist new default category if it doesn't already exist
    if (defaultCategory && !allCategories.includes(defaultCategory)) {
      setCustomCategories(prev => prev.includes(defaultCategory) ? prev : [...prev, defaultCategory]);
      if (mediaType === 'wallpaper' || mediaType === 'live-wallpaper' || mediaType === 'ringtone') {
        onNewCategory?.(mediaType, defaultCategory);
      }
    }

    setIsRunning(true);
    setDoneCount(0);
    setFailCount(0);
    setDupCount(0);

    // Fetch all existing wallpapers once and build a Set of existing imageUrls
    const existingWallpapers = await getAllWallpapers();
    const existingUrls = new Set(existingWallpapers.map(w => w.imageUrl));

    let done = 0;
    let fail = 0;
    let dups = 0;

    for (let idx = 0; idx < pending.length; idx++) {
      const item = pending[idx];
      updateItem(item.id, { status: 'uploading' });

      try {
        // Step 1: Upload file (skip if already uploaded during pre-analysis)
        let rawUrl = item.rawUrl;

        if (!rawUrl) {
          const formData = new FormData();
          formData.append('file', item.file);

          const uploadResponse = await fetch(getPoofAPIUrl('/api/upload-image'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-Wallet-Address': user.address,
              ...getPoofAPIHeaders(),
            },
            body: formData,
          });

          const uploadJson = await uploadResponse.json() as { success: boolean; data?: { url: string }; error?: { message: string } };
          if (!uploadJson.success || !uploadJson.data?.url) {
            throw new Error(uploadJson.error?.message || 'Upload failed');
          }
          rawUrl = uploadJson.data.url.split('?')[0];
        }

        // Step 2: Optionally get AI suggestion (skip if already done pre-upload)
        let finalTitle = item.title;
        let finalCategory = item.category;

        if (useAI && !item.aiAnalyzed) {
          try {
            if (currentKey.trim() && isOpenAIKey) {
              const result = await analyzeWallpaperImage(rawUrl, currentKey.trim());
              if (result) {
                if (result.title) finalTitle = result.title;
                if (result.category) finalCategory = result.category;
                updateItem(item.id, { title: finalTitle, category: finalCategory });
              }
            }
          } catch {
            // AI failed — use existing title/category silently
          }
        }

        // Step 3: Check for duplicate imageUrl before saving
        const finalImageUrl = `${rawUrl}?w=2160&q=95&auto=format&fit=crop&crop=center`;
        if (existingUrls.has(finalImageUrl)) {
          updateItem(item.id, { status: 'duplicate', hostedUrl: rawUrl });
          dups++;
          setDupCount(dups);
          continue;
        }

        // Step 4: Save to collection — always use the locked mediaType from props
        const wallpaperId = `wall-${Date.now()}-${idx}`;
        const thumbnailUrl = `${rawUrl}?w=600&q=85&auto=format&fit=crop&crop=center`;

        const ok = await setWallpapers(wallpaperId, {
          title: finalTitle.trim(),
          imageUrl: finalImageUrl,
          thumbnailUrl,
          category: finalCategory,
          price: (item.price === '' ? 1 : item.price) * 1_000_000,
          uploadedBy: Address.publicKey(ADMIN_ADDRESS),
          createdAt: Time.Now,
          downloadCount: 0,
          mediaType: mediaType,
          status: 'published',
          sizeBytes: item.file.size,
        } as any);

        if (!ok) throw new Error('Failed to save to collection');

        // Track the new URL so subsequent items in this same batch are also deduplicated
        existingUrls.add(finalImageUrl);

        updateItem(item.id, { status: 'done', hostedUrl: rawUrl });
        done++;
        setDoneCount(done);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        updateItem(item.id, { status: 'error', error: msg });
        fail++;
        setFailCount(fail);
      }
    }

    if (dups > 0) {
      toast.warning(`${dups} wallpaper${dups !== 1 ? 's' : ''} skipped (already exist)`);
    }

    setIsRunning(false);
    setShowSummary(true);
  };

  const handleClose = () => {
    if (isRunning) return;
    // Revoke all object URLs
    items.forEach(i => { try { URL.revokeObjectURL(i.localPreview); } catch { /* ignore */ } });
    setItems([]);
    setDoneCount(0);
    setFailCount(0);
    setDupCount(0);
    setShowSummary(false);
    setUseAI(false);
    setDefaultCategory('All');
    setDefaultPrice('');
    setIsDragging(false);
    setCustomCategories([]);
    onClose();
  };

  const pendingCount = items.filter(i => i.status === 'pending').length;
  const uploadingIdx = items.findIndex(i => i.status === 'uploading');
  const totalPending = items.filter(i => i.status === 'pending' || i.status === 'uploading').length;
  const progressNum = isRunning && items.length > 0
    ? Math.round(((items.length - totalPending) / items.length) * 100)
    : 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 3000,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 520,
              background: '#0a0a14',
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.09)',
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* ── Header ── */}
            <div style={{ padding: '14px 16px 0', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Back arrow button */}
                <motion.button
                  whileHover={{ scale: 1.1, background: 'rgba(139,92,246,0.25)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onNavigateToAdmin ?? handleClose}
                  disabled={isRunning}
                  title="Go back to Admin Panel"
                  style={{
                    width: 36, height: 36, flexShrink: 0,
                    borderRadius: 10,
                    background: 'rgba(139,92,246,0.12)',
                    border: '1px solid rgba(139,92,246,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    color: '#a78bfa',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <ArrowLeft size={18} />
                </motion.button>

                {/* Title + subtitle */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: '#a78bfa',
                    fontFamily: "'Syne', sans-serif", lineHeight: 1.1,
                    letterSpacing: '-0.01em',
                  }}>
                    Bulk Upload
                  </div>
                  <div style={{
                    fontSize: 12, color: 'rgba(200,200,210,0.5)',
                    fontFamily: "'Syne', sans-serif", marginTop: 2,
                  }}>
                    {items.length > 0 ? `${items.length} file${items.length !== 1 ? 's' : ''} queued` : getSectionName(mediaType)}
                  </div>
                </div>

                {/* Close button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleClose}
                    disabled={isRunning}
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: isRunning ? 'not-allowed' : 'pointer',
                      color: 'rgba(240,240,245,0.6)',
                    }}
                  >
                    <X size={14} />
                  </motion.button>
                </div>
              </div>

              {/* Divider */}
              <div style={{
                height: 1,
                background: 'rgba(255,255,255,0.08)',
                marginTop: 10, marginBottom: 0,
              }} />
            </div>

            {/* Summary overlay */}
            <AnimatePresence>
              {showSummary && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'absolute', inset: 0, zIndex: 10,
                    background: 'rgba(10,10,20,0.96)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 16,
                    borderRadius: '20px 20px 0 0',
                  }}
                >
                  <div style={{ fontSize: 48 }}>{failCount === 0 ? '🎉' : '⚠️'}</div>
                  <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: 22, color: '#f0f0f5', textAlign: 'center' }}>
                    Upload Complete
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{
                      padding: '10px 20px', borderRadius: 10,
                      background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)',
                      textAlign: 'center',
                    }}>
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, fontWeight: 700, color: '#4ade80' }}>{doneCount}</div>
                      <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Uploaded</div>
                    </div>
                    {dupCount > 0 && (
                      <div style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, fontWeight: 700, color: '#fbbf24' }}>{dupCount}</div>
                        <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Skipped</div>
                      </div>
                    )}
                    {failCount > 0 && (
                      <div style={{
                        padding: '10px 20px', borderRadius: 10,
                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                        textAlign: 'center',
                      }}>
                        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 24, fontWeight: 700, color: '#f87171' }}>{failCount}</div>
                        <div style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'Syne', sans-serif", marginTop: 2 }}>Failed</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {failCount > 0 && (
                      <motion.button
                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => { setShowSummary(false); }}
                        style={{
                          padding: '10px 20px', borderRadius: 10,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          color: '#f0f0f5', fontSize: 13, fontWeight: 700,
                          cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                        }}
                      >
                        Review Errors
                      </motion.button>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={handleClose}
                      style={{
                        padding: '10px 24px', borderRadius: 10,
                        background: 'rgba(139,92,246,0.85)',
                        border: '1px solid rgba(167,139,250,0.45)',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: 'pointer', fontFamily: "'Syne', sans-serif",
                      }}
                    >
                      Done
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px', minHeight: 0 }}>
              {/* Inner content card */}
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: '10px 12px 12px',
                marginBottom: 8,
              }}>
                {/* Drop zone */}
                <motion.div
                  onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  animate={{ borderColor: isDragging ? 'rgba(139,92,246,0.7)' : 'rgba(139,92,246,0.3)' }}
                  style={{
                    borderRadius: 10,
                    border: `2px dashed ${isDragging ? 'rgba(139,92,246,0.7)' : 'rgba(139,92,246,0.3)'}`,
                    background: isDragging ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.02)',
                    padding: items.length > 0 ? '10px' : '24px 16px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 6, cursor: 'pointer', transition: 'all 0.15s ease',
                    marginBottom: 10,
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'rgba(139,92,246,0.15)',
                    border: '1px solid rgba(139,92,246,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Upload size={14} color="#a78bfa" />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'rgba(240,240,245,0.7)', fontFamily: "'Syne', sans-serif" }}>
                      {items.length > 0 ? 'Add more files' : 'Drop files here'}
                    </div>
                    {items.length === 0 && (
                      <div style={{ fontSize: 10, color: 'rgba(240,240,245,0.3)', fontFamily: "'DM Mono', monospace", marginTop: 2 }}>
                        {typeLabel} files only
                      </div>
                    )}
                  </div>
                </motion.div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={acceptAttr}
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />

                {/* Batch defaults bar */}
                <div style={{
                  paddingTop: 8,
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    {/* Default category */}
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={labelStyle}>Category</label>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <select
                            value={allCategories.includes(defaultCategory) ? defaultCategory : (defaultCategory ? '__custom__' : '')}
                            onChange={e => {
                              if (e.target.value !== '__custom__') setDefaultCategory(e.target.value);
                            }}
                            disabled={isRunning}
                            style={{
                              width: '100%', padding: '6px 24px 6px 8px',
                              borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
                              background: 'rgba(255,255,255,0.05)', color: '#f0f0f5',
                              fontSize: 11, outline: 'none', boxSizing: 'border-box',
                              fontFamily: "'Syne', sans-serif",
                              cursor: isRunning ? 'not-allowed' : 'pointer',
                              appearance: 'none',
                              WebkitAppearance: 'none',
                              opacity: isRunning ? 0.6 : 1,
                            } as React.CSSProperties}
                          >
                            {allCategories.length === 0 && (
                              <option value="" disabled>No categories yet</option>
                            )}
                            {allCategories.map(cat => (
                              <option key={cat} value={cat} style={{ background: '#0a0a14', color: '#f0f0f5' }}>
                                {cat}
                              </option>
                            ))}
                            {defaultCategory && !allCategories.includes(defaultCategory) && (
                              <option value="__custom__" style={{ background: '#0a0a14', color: '#a78bfa' }}>
                                {defaultCategory}
                              </option>
                            )}
                          </select>
                          <div style={{
                            position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)',
                            pointerEvents: 'none', color: 'rgba(167,139,250,0.5)',
                          }}>
                            <ChevronDown size={11} />
                          </div>
                        </div>
                        <CategoryNewButton
                          disabled={isRunning}
                          onAdd={cat => {
                            setCustomCategories(prev => prev.includes(cat) ? prev : [...prev, cat]);
                            if (mediaType === 'wallpaper' || mediaType === 'live-wallpaper' || mediaType === 'ringtone') {
                              onNewCategory?.(mediaType, cat);
                            }
                            setDefaultCategory(cat);
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ flex: '1 1 100px' }}>
                      <label style={labelStyle}>Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={defaultPrice}
                        onChange={e => {
                          const raw = e.target.value;
                          if (raw === '') { setDefaultPrice(''); return; }
                          const v = parseFloat(raw);
                          if (!Number.isNaN(v) && v >= 0) setDefaultPrice(v);
                        }}
                        placeholder="0.00"
                        disabled={isRunning}
                        style={{
                          width: '100%',
                          background: 'rgba(139,92,246,0.12)',
                          border: '1px solid rgba(139,92,246,0.35)',
                          color: '#f0f0f5',
                          fontSize: 12, fontWeight: 700,
                          fontFamily: "'DM Mono', monospace",
                          textAlign: 'center', borderRadius: 8, padding: '6px 8px',
                          WebkitAppearance: 'none', boxSizing: 'border-box', outline: 'none',
                          cursor: isRunning ? 'not-allowed' : 'auto',
                          opacity: isRunning ? 0.6 : 1,
                        } as React.CSSProperties}
                      />
                    </div>

                    {/* Global AI button (images only) */}
                    {(mediaType === 'wallpaper' || mediaType === 'icon') && (() => {
                      const isAnyAnalyzing = items.some(i => i.isAnalyzing);
                      const unanalyzed = items.filter(i => i.status === 'pending' && !i.isAnalyzing && !i.aiAnalyzed);
                      const disabled = isRunning || items.length === 0 || isAnyAnalyzing || unanalyzed.length === 0;
                      return (
                        <div style={{ flexShrink: 0 }}>
                          <label style={labelStyle}>AI Suggest</label>
                          <motion.button
                            whileTap={disabled ? {} : { scale: 0.95 }}
                            whileHover={disabled ? {} : { scale: 1.04 }}
                            onClick={() => {
                              if (disabled) return;
                              setUseAI(true);
                              analyzeItems(unanalyzed);
                            }}
                            title={
                              items.length === 0
                                ? 'Сначала добавьте изображения'
                                : unanalyzed.length === 0
                                  ? 'Все изображения уже проанализированы'
                                  : `Запустить AI для ${unanalyzed.length} изображений`
                            }
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px', borderRadius: 8,
                              background: disabled
                                ? 'rgba(20,15,40,0.5)'
                                : 'rgba(139,92,246,0.18)',
                              border: disabled
                                ? '1px solid rgba(139,92,246,0.15)'
                                : '1px solid rgba(139,92,246,0.5)',
                              color: disabled ? 'rgba(240,240,245,0.35)' : '#e2d9f3',
                              fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer',
                              fontFamily: "'Syne', sans-serif",
                              transition: 'all 0.15s ease',
                              opacity: disabled ? 0.5 : 1,
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {isAnyAnalyzing ? (
                              <>
                                <Loader2 size={11} style={{ animation: 'spin 1s linear infinite', color: '#a78bfa' }} />
                                <span>AI...</span>
                              </>
                            ) : (
                              <>
                                <span style={{ color: disabled ? 'rgba(234,179,8,0.3)' : '#FFD700', fontSize: 12 }}>✦</span>
                                <span>AI {unanalyzed.length > 0 ? `(${unanalyzed.length})` : ''}</span>
                              </>
                            )}
                          </motion.button>
                        </div>
                      );
                    })()}

                    {/* Apply defaults */}
                    {items.length > 0 && (
                      <motion.button
                        whileHover={isRunning ? {} : { scale: 1.02 }}
                        whileTap={isRunning ? {} : { scale: 0.98 }}
                        onClick={applyDefaults}
                        disabled={isRunning}
                        style={{
                          flexShrink: 0,
                          padding: '6px 10px', borderRadius: 8,
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'rgba(240,240,245,0.6)', fontSize: 11, fontWeight: 700,
                          cursor: isRunning ? 'not-allowed' : 'pointer',
                          fontFamily: "'Syne', sans-serif",
                        }}
                      >
                        Apply to All
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>

              {/* Queue list */}
              {items.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map((item, idx) => (
                    <BulkItemRow
                      key={item.id}
                      item={item}
                      index={idx}
                      allCategories={allCategories}
                      isRunning={isRunning}
                      mediaType={mediaType}
                      onUpdate={patch => updateItem(item.id, patch)}
                      onRemove={() => removeItem(item.id)}
                      onAddCategory={cat => {
                        setCustomCategories(prev => prev.includes(cat) ? prev : [...prev, cat]);
                        if (mediaType === 'wallpaper' || mediaType === 'live-wallpaper' || mediaType === 'ringtone') {
                          onNewCategory?.(mediaType, cat);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* ── Footer: Progress + Upload button ── */}
            <div style={{
              padding: '10px 16px 14px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              flexShrink: 0,
            }}>
              {isRunning && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'DM Mono', monospace" }}>
                      Uploading {uploadingIdx >= 0 ? uploadingIdx + 1 : doneCount + failCount + 1} / {items.length}
                    </span>
                    <span style={{ fontSize: 11, color: 'rgba(167,139,250,0.8)', fontFamily: "'DM Mono', monospace" }}>
                      {progressNum}%
                    </span>
                  </div>
                  <div style={{
                    height: 4, borderRadius: 2,
                    background: 'rgba(255,255,255,0.08)',
                    overflow: 'hidden',
                  }}>
                    <motion.div
                      animate={{ width: `${progressNum}%` }}
                      transition={{ duration: 0.4 }}
                      style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                        borderRadius: 2,
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <motion.button
                  whileHover={isRunning || pendingCount === 0 ? {} : { scale: 1.02, boxShadow: '0 0 18px rgba(139,92,246,0.4)' }}
                  whileTap={isRunning || pendingCount === 0 ? {} : { scale: 0.98 }}
                  onClick={handleUploadAll}
                  disabled={isRunning || pendingCount === 0}
                  style={{
                    flex: 1,
                    padding: '8px 12px', borderRadius: 10,
                    background: (isRunning || pendingCount === 0) ? 'rgba(139,92,246,0.4)' : 'rgba(139,92,246,0.85)',
                    border: '1px solid rgba(167,139,250,0.45)',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                    cursor: (isRunning || pendingCount === 0) ? 'not-allowed' : 'pointer',
                    fontFamily: "'Syne', sans-serif",
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    opacity: (isRunning || pendingCount === 0) ? 0.65 : 1,
                    transition: 'all 0.15s ease',
                  }}
                >
                  {isRunning ? (
                    <>
                      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={14} />
                      Upload {pendingCount > 0 ? `${pendingCount} ` : ''}File{pendingCount !== 1 ? 's' : ''}
                    </>
                  )}
                </motion.button>

                {/* Cancel text link */}
                <button
                  onClick={handleClose}
                  disabled={isRunning}
                  style={{
                    background: 'none', border: 'none', padding: '8px 6px',
                    color: 'rgba(240,240,245,0.4)',
                    fontSize: 12, fontWeight: 600,
                    fontFamily: "'Syne', sans-serif",
                    cursor: isRunning ? 'not-allowed' : 'pointer',
                    transition: 'color 0.15s ease',
                    flexShrink: 0,
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.7)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.4)'; }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Sub-component for each row in the queue
interface BulkItemRowProps {
  item: BulkItem;
  index: number;
  allCategories: string[];
  isRunning: boolean;
  mediaType: MediaType;
  onUpdate: (patch: Partial<BulkItem>) => void;
  onRemove: () => void;
  onAddCategory: (cat: string) => void;
}

const BulkItemRow: React.FC<BulkItemRowProps> = ({ item, allCategories, isRunning, mediaType, onUpdate, onRemove, onAddCategory }) => {
  const [showRowCustomInput, setShowRowCustomInput] = React.useState(false);
  const [rowCustomInput, setRowCustomInput] = React.useState('');
  const isEditable = item.status === 'pending' && !item.isAnalyzing;
  const statusColor = {
    pending: 'rgba(255,255,255,0.2)',
    uploading: '#a78bfa',
    done: '#4ade80',
    error: '#f87171',
    duplicate: '#fbbf24',
  }[item.status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      style={{
        display: 'flex', gap: 10, alignItems: 'stretch',
        padding: 10, borderRadius: 10,
        background: item.status === 'error'
          ? 'rgba(239,68,68,0.06)'
          : item.status === 'done'
            ? 'rgba(34,197,94,0.05)'
            : item.status === 'duplicate'
              ? 'rgba(234,179,8,0.06)'
              : 'rgba(255,255,255,0.03)',
        border: `1px solid ${item.status === 'error'
          ? 'rgba(239,68,68,0.2)'
          : item.status === 'done'
            ? 'rgba(34,197,94,0.15)'
            : item.status === 'duplicate'
              ? 'rgba(234,179,8,0.2)'
              : 'rgba(255,255,255,0.07)'}`,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: 72, height: 54, borderRadius: 7, flexShrink: 0,
        overflow: 'hidden', background: 'rgba(255,255,255,0.05)',
        position: 'relative',
      }}>
        {mediaType === 'live-wallpaper' ? (
          <video
            src={item.localPreview}
            muted
            playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : mediaType === 'ringtone' ? (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(167,139,250,0.1)' }}>
            <Bell size={22} color="rgba(167,139,250,0.6)" />
          </div>
        ) : (
          <img
            src={item.localPreview}
            alt={item.title}
            loading="lazy"
            decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        {item.status === 'uploading' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Loader2 size={18} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        {item.status === 'done' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircle2 size={20} color="#4ade80" />
          </div>
        )}
        {item.status === 'duplicate' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              padding: '3px 7px', borderRadius: 5,
              background: 'rgba(234,179,8,0.85)',
              color: '#000', fontSize: 9, fontWeight: 800,
              fontFamily: "'Syne', sans-serif",
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}>
              Already exists
            </div>
          </div>
        )}
        {item.status === 'error' && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertCircle size={20} color="#f87171" />
          </div>
        )}
      </div>

      {/* Fields */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        {/* Title input with fixed-height label row + analyzing indicator */}
        <div>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            height: 20, marginBottom: 3,
          }}>
            <label style={{
              fontSize: 9, fontWeight: 700,
              color: 'rgba(167,139,250,0.7)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              fontFamily: "'Syne', sans-serif",
            }}>
              Title
            </label>
            {item.isAnalyzing && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Loader2 size={9} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{
                  fontSize: 9, color: 'rgba(167,139,250,0.7)',
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: '0.04em',
                }}>
                  AI...
                </span>
              </div>
            )}
            {item.aiAnalyzed && !item.isAnalyzing && (
              <span style={{ color: '#FFD700', fontSize: 9 }}>✦</span>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <input
              value={item.isAnalyzing ? '' : item.title}
              onChange={e => onUpdate({ title: e.target.value })}
              disabled={!isEditable}
              placeholder={item.isAnalyzing ? '' : 'Title'}
              style={{
                ...inputStyle,
                padding: '5px 8px',
                fontSize: 11,
                opacity: isEditable ? 1 : 0.65,
                background: item.isAnalyzing
                  ? 'rgba(139,92,246,0.08)'
                  : isEditable ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: item.isAnalyzing
                  ? '1px solid rgba(139,92,246,0.35)'
                  : '1px solid rgba(255,255,255,0.1)',
              }}
            />
            {item.isAnalyzing && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '0 8px',
                pointerEvents: 'none',
              }}>
                <Loader2 size={11} color="#a78bfa" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                <span style={{
                  fontSize: 11, color: 'rgba(167,139,250,0.7)',
                  fontFamily: "'DM Mono', monospace",
                  letterSpacing: '0.04em',
                }}>
                  AI analyzing...
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Category */}
          <div style={{ flex: 1, position: 'relative' }}>
            {showRowCustomInput ? (
              <input
                autoFocus
                value={rowCustomInput}
                onChange={e => setRowCustomInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && rowCustomInput.trim()) {
                    const newCat = rowCustomInput.trim();
                    onAddCategory(newCat);
                    onUpdate({ category: newCat });
                    setRowCustomInput('');
                    setShowRowCustomInput(false);
                  } else if (e.key === 'Escape') {
                    setRowCustomInput('');
                    setShowRowCustomInput(false);
                  }
                }}
                onBlur={() => {
                  if (rowCustomInput.trim()) {
                    const newCat = rowCustomInput.trim();
                    onAddCategory(newCat);
                    onUpdate({ category: newCat });
                  }
                  setRowCustomInput('');
                  setShowRowCustomInput(false);
                }}
                placeholder="New category..."
                style={{
                  ...inputStyle,
                  padding: '5px 8px',
                  background: 'rgba(139,92,246,0.12)',
                  border: '1px solid rgba(139,92,246,0.4)',
                }}
              />
            ) : (
              <>
                <select
                  value={item.category || (allCategories.length > 0 ? allCategories[0] : '__new__')}
                  onChange={e => {
                    if (e.target.value === '__new__') {
                      setShowRowCustomInput(true);
                    } else {
                      onUpdate({ category: e.target.value });
                    }
                  }}
                  disabled={!isEditable}
                  style={{
                    ...inputStyle,
                    padding: '5px 24px 5px 8px',
                    appearance: 'none',
                    cursor: isEditable ? 'pointer' : 'default',
                    opacity: isEditable ? 1 : 0.65,
                    background: isEditable ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                  }}
                >
                  {allCategories.map(cat => (
                    <option key={cat} value={cat} style={{ background: '#0a0a14' }}>{cat}</option>
                  ))}
                  <option value="__new__" style={{ background: '#0a0a14' }}>+ New category</option>
                </select>
                <ChevronDown size={11} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', color: 'rgba(240,240,245,0.3)', pointerEvents: 'none' }} />
              </>
            )}
          </div>

          {/* Price */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.price}
              disabled={!isEditable}
              onChange={e => {
                if (!isEditable) return;
                const raw = e.target.value;
                if (raw === '') { onUpdate({ price: '' }); return; }
                const v = parseFloat(raw);
                if (!Number.isNaN(v) && v >= 0) onUpdate({ price: v });
              }}
              placeholder="Price"
              style={{
                width: 64, padding: '4px 6px', borderRadius: 7,
                background: 'rgba(139,92,246,0.10)',
                border: '1px solid rgba(139,92,246,0.28)',
                color: '#f0f0f5',
                fontSize: 11, fontWeight: 700,
                fontFamily: "'DM Mono', monospace",
                opacity: isEditable ? 1 : 0.65,
                textAlign: 'center', outline: 'none', boxSizing: 'border-box',
                WebkitAppearance: 'none',
              }}
            />
            <span style={{ fontSize: 9, color: 'rgba(139,92,246,0.6)', fontFamily: "'DM Mono', monospace", marginLeft: 1 }}>SKR</span>
          </div>

          {/* Status indicator */}
          <div style={{
            flexShrink: 0, width: 24, height: 24,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {item.status === 'pending' && !isRunning && !item.isAnalyzing && (
              <motion.button
                whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                onClick={onRemove}
                style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'rgba(248,113,113,0.7)',
                }}
              >
                <Trash2 size={11} />
              </motion.button>
            )}
            {item.status === 'uploading' && (
              <Loader2 size={14} color={statusColor} style={{ animation: 'spin 1s linear infinite' }} />
            )}
            {item.status === 'done' && <CheckCircle2 size={16} color={statusColor} />}
            {item.status === 'duplicate' && (
              <span title="Already exists in catalog" style={{
                fontSize: 9, fontWeight: 800, color: '#fbbf24',
                fontFamily: "'Syne', sans-serif",
                background: 'rgba(234,179,8,0.15)',
                border: '1px solid rgba(234,179,8,0.3)',
                borderRadius: 4, padding: '2px 4px',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}>
                DUP
              </span>
            )}
            {item.status === 'error' && (
              <span title={item.error ?? ''} style={{ display: 'flex', alignItems: 'center' }}>
                <AlertCircle size={16} color={statusColor} />
              </span>
            )}
          </div>
        </div>

        {/* Error message */}
        {item.status === 'error' && item.error && (
          <div style={{
            fontSize: 10, color: '#f87171',
            fontFamily: "'DM Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.error}
          </div>
        )}
      </div>
    </motion.div>
  );
};
